import React, { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { DealerComparisonPricesPayload } from "../../queries/useGetDealerComparisonPricesByTireSize";
import { PricingAnalysisFormState } from "../../stores/slices/pricingAnalysisSlice";
import { PrimaryButton, SquareButton } from "../../ui/Button/Button";
import { Input } from "../../ui/Input/Input";
import { InputGroup } from "../../ui/IputGroup/IputGroup";
import { Select } from "../../ui/Select/Select";
import { CrossIcon } from "../../ui/icons/CrossIcon";
import { useForm } from "../../util/useForm";
import { useBrands } from "./useBrands";
import { usePatchPricing } from "./usePatchPricing";
import { useSeasons } from "./useSeasons";

type PricingAnalysisFormProps = {
  data?: DealerComparisonPricesPayload;
  state: PricingAnalysisFormState;
};

const schema = z.object({
  brand: z.string().optional().default(""),
  season: z.string().optional().default(""),
  tireSize: z.string(),
});

export const PricingAnalysisForm: FunctionComponent<
  PricingAnalysisFormProps
> = ({ data, state: { brand, season, tireSize, version } }) => {
  const patch = usePatchPricing();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [handleForm] = useForm({
    noReset: true,
    onSubmit: patch,
    schema,
  });
  const brands = useBrands(data);
  const seasons = useSeasons(data);
  return (
    <div className="grid gap-2 md:flex">
      {!!data && (
        <>
          <Select
            onChange={(event): void => {
              patch({ brand: event.target.value });
            }}
            aria-label="brandFilter"
            name="brand"
            value={brand}
          >
            <option value="">{t("pricingAnalysis.allBrands")}</option>
            {brands.map((brand) => (
              <option key={brand}>{brand}</option>
            ))}
          </Select>
          <Select
            onChange={(event): void => {
              patch({ season: event.target.value });
            }}
            aria-label="seasonFilter"
            name="season"
            value={season}
          >
            <option value="">{t("season.1")}</option>
            {seasons.map(({ disabled, label, value }) => (
              <option disabled={disabled} key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </>
      )}
      <form className="flex md:ml-auto" noValidate onSubmit={handleForm}>
        <InputGroup className="rounded-r-none border-r-none">
          <Input
            aria-label="selectedTireSize"
            defaultValue={tireSize}
            key={version}
            name="tireSize"
            placeholder={t("pricingAnalysis.tireSize")}
            ref={inputRef}
          />
          {tireSize && (
            <SquareButton
              onClick={(): void => {
                patch({ tireSize: "" });
              }}
              aria-label="clearTireSizeInput"
              className="rounded-none"
            >
              <CrossIcon />
            </SquareButton>
          )}
        </InputGroup>
        <PrimaryButton
          aria-label="pricingAnalysisSearch"
          className="rounded-l-none md:px-8"
          type="submit"
        >
          {t("pricingAnalysis.search")}
        </PrimaryButton>
      </form>
    </div>
  );
};
